import Table, { TableProps } from "@amzn/awsui-components-react/polaris/table";
import Header from "@amzn/awsui-components-react/polaris/header";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import useQueryGetReport from "Api/useQueryGetReport";
import Loading from "components/Loading";
import { useEffect, useMemo, useState } from "react";
import { compareCells } from "./reportSorting";

interface ReportTableProps {
  readonly reportKey: string;
}

const DEFAULT_SORT_HEADER = "count";

const ReportTable = ({ reportKey }: ReportTableProps) => {
  const { isLoading, error, data: reportData } = useQueryGetReport(reportKey);

  const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<string[]>>();
  const [sortingDescending, setSortingDescending] = useState(true);

  useEffect(() => {
    setSortingColumn(undefined);
    setSortingDescending(true);
  }, [reportKey]);

  const columnDefinitions = useMemo<TableProps.ColumnDefinition<string[]>[]>(
    () =>
      (reportData?.headers ?? []).map((header, index) => ({
        id: `col-${index}`,
        header,
        // eslint-disable-next-line security/detect-object-injection -- safe numeric array index
        cell: (row) => row[index] ?? "",
        // eslint-disable-next-line security/detect-object-injection -- safe numeric array index
        sortingComparator: (a, b) => compareCells(a[index] ?? "", b[index] ?? ""),
      })),
    [reportData?.headers]
  );

  const defaultSortingColumn = useMemo(() => {
    const countIndex = (reportData?.headers ?? []).findIndex(
      (header) => header.trim().toLowerCase() === DEFAULT_SORT_HEADER
    );
    // eslint-disable-next-line security/detect-object-injection -- safe numeric array index
    return countIndex >= 0 ? columnDefinitions[countIndex] : undefined;
  }, [reportData?.headers, columnDefinitions]);

  const activeSortingColumn = sortingColumn ?? defaultSortingColumn;

  const sortedRows = useMemo(() => {
    const rows = reportData?.rows ?? [];
    const comparator = activeSortingColumn?.sortingComparator;
    if (!comparator) {
      return rows;
    }
    const direction = sortingDescending ? -1 : 1;
    return [...rows].sort((a, b) => comparator(a, b) * direction);
  }, [reportData?.rows, activeSortingColumn, sortingDescending]);

  if (error) {
    return <Alert type="error">{error}</Alert>;
  }

  return (
    <Loading isLoading={isLoading} reason="Loading report data...">
      {reportData && (
        <Table
          header={
            <Header variant="h2" counter={`(${reportData.rows.length} rows)`}>
              {reportData.key}
            </Header>
          }
          columnDefinitions={columnDefinitions}
          items={sortedRows}
          sortingColumn={activeSortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setSortingDescending(detail.isDescending ?? false);
          }}
          resizableColumns
          wrapLines
          empty="This report has no data rows."
        />
      )}
    </Loading>
  );
};

export default ReportTable;
